/// <reference lib="dom" />
import React, { useCallback, useRef, useState } from 'react';
import { Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { theme, useThemeColors } from '../../../../theme';
import { s, v, fs, borderWidth } from '../../../../utils/libs';

type Props = {
  previewUri?: string;
  onUploaded: (url: string, localUri?: string) => void;
  onDiscard: () => void;
  onProcessingChange?: (processing: boolean) => void;
};

const MAX_WIDTH = 1440;
const MAX_HEIGHT = 1440;
const QUALITY = 0.75;

/**
 * Resize image using Canvas API and return base64
 */
const resizeImageToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = document.createElement('img');
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      let { width, height } = img;

      // Scale down if needed
      if (width > MAX_WIDTH || height > MAX_HEIGHT) {
        const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
        width = Math.floor(width * ratio);
        height = Math.floor(height * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context unavailable'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      // Get base64 without the data:image/jpeg;base64, prefix
      const dataUrl = canvas.toDataURL('image/jpeg', QUALITY);
      const base64 = dataUrl.split(',')[1];
      resolve(base64);
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image'));
    };

    img.src = objectUrl;
  });
};

export default function ProductImagePicker({
  previewUri,
  onUploaded,
  onDiscard,
  onProcessingChange,
}: Props) {
  const colors = useThemeColors();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [localPreviewUri, setLocalPreviewUri] = useState<string | undefined>();
  const [processing, setProcessing] = useState(false);

  const triggerFilePicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Reset input so same file can be selected again
      e.target.value = '';

      setProcessing(true);
      onProcessingChange?.(true);

      try {
        // Create preview URL
        const previewUrl = URL.createObjectURL(file);
        setLocalPreviewUri(previewUrl);

        // Resize and convert to base64
        const base64 = await resizeImageToBase64(file);
        onUploaded(base64, previewUrl);
      } catch (err: any) {
        console.error('Image processing failed:', err);
        alert(err?.message || 'Failed to process image');
      } finally {
        setProcessing(false);
        onProcessingChange?.(false);
      }
    },
    [onUploaded, onProcessingChange],
  );

  const handleDiscard = useCallback(() => {
    if (localPreviewUri) {
      URL.revokeObjectURL(localPreviewUri);
    }
    setLocalPreviewUri(undefined);
    onDiscard();
  }, [localPreviewUri, onDiscard]);

  const handleReplace = useCallback(() => {
    handleDiscard();
    triggerFilePicker();
  }, [handleDiscard, triggerFilePicker]);

  const displayUri = localPreviewUri || previewUri;

  return (
    <View style={{ gap: s(10) }}>
      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange as any}
        style={{ display: 'none' }}
      />
      {displayUri ? (
        /* Preview with overlay buttons */
        <View style={{ marginTop: v(10) }}>
          <Image
            source={{
              uri: (() => {
                const raw = displayUri;
                if (
                  raw.startsWith('http') ||
                  raw.startsWith('blob:') ||
                  raw.startsWith('data:')
                )
                  return raw;
                // base64 without prefix
                if (raw.length > 100) {
                  return `data:image/jpeg;base64,${raw}`;
                }
                return raw;
              })(),
            }}
            style={styles.previewImage}
            resizeMode="contain"
          />
          <View style={styles.overlayRow}>
            <TouchableOpacity
              onPress={handleDiscard}
              style={styles.overlayChip}
              disabled={processing}>
              <Text style={styles.overlayChipText}>Discard</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={handleReplace}
              style={styles.overlayChip}
              disabled={processing}>
              <Text style={styles.overlayChipText}>Replace</Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : (
        /* Empty state - pick button */
        <TouchableOpacity
          onPress={triggerFilePicker}
          disabled={processing}
          style={[styles.pickButton, { borderColor: colors.borderGray300 }]}>
          <Text style={[styles.pickButtonText, { color: colors.text }]}>
            {processing ? 'Processing...' : 'Select image from device'}
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  pickButton: {
    height: v(200),
    borderRadius: s(12),
    borderWidth: borderWidth(1),
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pickButtonText: {
    fontSize: fs(14),
    fontWeight: '500',
  },
  previewImage: {
    width: '100%',
    height: v(200),
    borderRadius: s(12),
  },
  overlayRow: {
    position: 'absolute',
    top: v(10),
    left: s(10),
    right: s(10),
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  overlayChip: {
    backgroundColor: 'rgba(0,0,0,0.55)',
    paddingHorizontal: s(10),
    paddingVertical: s(6),
    borderRadius: s(12),
  },
  overlayChipText: {
    color: theme.colors.textInverse,
    fontSize: fs(12),
    fontWeight: '600',
  },
});
